import { useTheme } from '../contexts/ThemeContext';
import { COLOR_THEMES } from '../contexts/ThemeContext';
import { useEffect, useState, useRef, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BackgroundWrapper({ children }) {
  const { colorTheme, isDarkMode, customizationsLoaded, getCurrentCustomization, previewMode } = useTheme();
  
  // Image loading states
  const [bgImageUrl, setBgImageUrl] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const hasLoggedError = useRef(false);
  const preloadedImageRef = useRef(null);
  
  const theme = COLOR_THEMES[colorTheme];
  const colors = theme ? (isDarkMode ? theme.dark : theme.light) : null;
  
  // Get current customization (includes preview mode support)
  const customization = getCurrentCustomization();
  
  // Preload image helper
  const preloadImage = useCallback((url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        preloadedImageRef.current = img;
        resolve(img);
      };
      img.onerror = (err) => {
        reject(err);
      };
      img.src = url;
    });
  }, []);
  
  // Load background image if customization has one
  useEffect(() => {
    const loadBgImage = async () => {
      const imageId = customization?.background?.image_id;
      
      if (!imageId) {
        setBgImageUrl(null);
        setImageLoaded(false);
        setImageError(false);
        return;
      }
      
      // Reset states for new image
      setImageLoaded(false);
      setImageError(false);
      hasLoggedError.current = false;
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/theme-customizations/image/${imageId}`);
        if (response.ok) {
          const data = await response.json();
          const imageUrl = data.data_url;
          
          // Preload the image before setting URL
          try {
            await preloadImage(imageUrl);
            setBgImageUrl(imageUrl);
            setImageLoaded(true);
            console.log('🎨 Background image loaded:', imageId);
          } catch (imgErr) {
            if (!hasLoggedError.current) {
              console.error('🎨 Background image failed to load:', imgErr);
              hasLoggedError.current = true;
            }
            setImageError(true);
            setBgImageUrl(null);
          }
        } else {
          if (!hasLoggedError.current) {
            console.error('🎨 Failed to fetch background image:', response.status);
            hasLoggedError.current = true;
          }
          setImageError(true);
          setBgImageUrl(null);
        }
      } catch (error) {
        if (!hasLoggedError.current) {
          console.error('🎨 Error loading background image:', error);
          hasLoggedError.current = true;
        }
        setImageError(true);
        setBgImageUrl(null);
      }
    };
    
    if (customizationsLoaded || previewMode) {
      loadBgImage();
    }
  }, [customization?.background?.image_id, customizationsLoaded, previewMode, preloadImage]);
  
  // Apply background styles
  useEffect(() => {
    if (!colors) return;
    
    const root = document.documentElement;
    
    // Mark theme as loaded (enables CSS transitions after initial render)
    root.classList.add('theme-loaded');
    
    // Determine if we have custom background settings
    const hasCustomBg = customization?.background?.color || 
                        customization?.background?.image_id;
    
    // Set/remove the data-custom-bg attribute to control CSS specificity
    if (hasCustomBg) {
      root.setAttribute('data-custom-bg', 'true');
      document.body.setAttribute('data-custom-bg', 'true');
    } else {
      root.removeAttribute('data-custom-bg');
      document.body.removeAttribute('data-custom-bg');
    }
    
    // Clean up existing elements
    const existingStyle = document.getElementById('dynamic-theme-styles');
    if (existingStyle) existingStyle.remove();
    
    const existingOverlay = document.getElementById('theme-background-overlay');
    if (existingOverlay) existingOverlay.remove();
    
    const existingColorLayer = document.getElementById('theme-background-color-layer');
    if (existingColorLayer) existingColorLayer.remove();
    
    const existingFallback = document.getElementById('theme-background-fallback');
    if (existingFallback) existingFallback.remove();
    
    // If no custom background, let CSS handle it
    if (!hasCustomBg) {
      console.log('🎨 No custom background, using CSS defaults');
      return;
    }
    
    // Get background colors - support solid or gradient
    const bgColor = customization?.background?.color || colors.bgGradientStart;
    const useGradient = customization?.background?.use_gradient ?? false;
    const bgColorEnd = useGradient 
      ? (customization?.background?.color_end || bgColor)
      : bgColor;
    
    // Build the background value
    const backgroundValue = useGradient
      ? `linear-gradient(to bottom, ${bgColor} 0%, ${bgColorEnd} 100%)`
      : bgColor;
    
    // Check if we should show an image
    const hasCustomImage = customization?.background?.image_id;
    const shouldShowImage = hasCustomImage && bgImageUrl && imageLoaded && !imageError;
    
    // Layer 1: Color/Gradient background (always shown as base)
    const colorLayer = document.createElement('div');
    colorLayer.id = 'theme-background-color-layer';
    colorLayer.style.cssText = `
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      z-index: -10000 !important;
      pointer-events: none !important;
      background: ${backgroundValue} !important;
    `;
    
    if (document.body.firstChild) {
      document.body.insertBefore(colorLayer, document.body.firstChild);
    } else {
      document.body.appendChild(colorLayer);
    }
    
    // If we have an image configured but it's loading or errored, show fallback
    if (hasCustomImage && !shouldShowImage) {
      // Show a subtle fallback while image loads or if it failed
      const fallbackLayer = document.createElement('div');
      fallbackLayer.id = 'theme-background-fallback';
      
      // Use a subtle pattern/gradient as fallback
      const fallbackGradient = isDarkMode 
        ? 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.2) 100%)'
        : 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 100%)';
      
      fallbackLayer.style.cssText = `
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        z-index: -9999 !important;
        pointer-events: none !important;
        background: ${fallbackGradient} !important;
        opacity: ${imageError ? 0.3 : 0.1} !important;
      `;
      
      document.body.insertBefore(fallbackLayer, colorLayer.nextSibling);
      
      if (imageError) {
        console.log('🎨 Showing fallback overlay (image failed to load)');
      } else {
        console.log('🎨 Showing fallback overlay (image loading...)');
      }
    }
    
    // Layer 2: Image overlay (only if loaded successfully)
    if (shouldShowImage) {
      const opacity = customization?.background?.opacity ?? 1.0;
      const position = customization?.background?.position || 'center';
      const size = customization?.background?.size || 'cover';
      
      const overlay = document.createElement('div');
      overlay.id = 'theme-background-overlay';
      overlay.style.cssText = `
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        z-index: -9999 !important;
        pointer-events: none !important;
        background-image: url(${bgImageUrl}) !important;
        background-color: transparent !important;
        background-size: ${size} !important;
        background-position: ${position} !important;
        background-repeat: no-repeat !important;
        background-attachment: fixed !important;
        opacity: ${opacity} !important;
      `;
      
      document.body.insertBefore(overlay, colorLayer.nextSibling);
      console.log('🎨 Applied background image overlay, opacity:', opacity);
    }
    
    // Create style tag to make containers transparent
    const styleTag = document.createElement('style');
    styleTag.id = 'dynamic-theme-styles';
    styleTag.innerHTML = `
      html[data-custom-bg], 
      html[data-custom-bg] body,
      html[data-custom-bg] #root, 
      html[data-custom-bg] .App, 
      html[data-custom-bg] .page-container, 
      html[data-custom-bg] .min-h-screen {
        background: transparent !important;
        background-image: none !important;
      }
    `;
    document.head.appendChild(styleTag);
    
  }, [colorTheme, isDarkMode, colors, bgImageUrl, customization, imageLoaded, imageError]);
  
  if (!colors) return <>{children}</>;
  
  return <>{children}</>;
}
